/*
 * ZELZIS Product Review Carousel
 *
 * Product-linked reviews are displayed first in the saved product metafield order.
 * Remaining reviews are shuffled for this page view and explicitly show which product
 * the review was originally written for. Only five cards are inserted at a time; more
 * cards are added as the visitor scrolls near the end of the horizontal carousel.
 *
 * This script uses DOM and scrolling APIs only. It makes no network requests and loads
 * no external code. Images receive their src only when a card is inserted.
 */
(function () {
  'use strict';

  var CAROUSEL_SELECTOR = '[data-zelzis-review-carousel]';
  var INITIALIZED_ATTRIBUTE = 'data-zpr-initialized';
  var CARD_SELECTOR = '[data-zpr-card]';
  var NEAR_END_PX = 260;

  function asArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function shuffle(items) {
    var result = items.slice();
    var index;

    for (index = result.length - 1; index > 0; index -= 1) {
      var swapIndex = Math.floor(Math.random() * (index + 1));
      var temporary = result[index];
      result[index] = result[swapIndex];
      result[swapIndex] = temporary;
    }

    return result;
  }

  function getTemplateCard(template) {
    if (!template || !template.content || !template.content.firstElementChild) {
      return null;
    }

    return template.content.firstElementChild.cloneNode(true);
  }

  function activateImage(card) {
    var image = card.querySelector('[data-zpr-src]');

    if (!image) {
      return;
    }

    var imageSource = image.getAttribute('data-zpr-src');
    if (imageSource) {
      image.setAttribute('src', imageSource);
      image.removeAttribute('data-zpr-src');
    }
  }

  function initializeCarousel(carousel) {
    if (!carousel || carousel.getAttribute(INITIALIZED_ATTRIBUTE) === 'true') {
      return;
    }

    var track = carousel.querySelector('[data-zpr-track]');
    var previousButton = carousel.querySelector('[data-zpr-prev]');
    var nextButton = carousel.querySelector('[data-zpr-next]');
    var controls = carousel.querySelector('[data-zpr-controls]');

    if (!track || !previousButton || !nextButton) {
      return;
    }

    var batchSize = parseInt(carousel.getAttribute('data-zpr-batch-size'), 10) || 5;
    var priorityTemplates = asArray(carousel.querySelectorAll('template[data-zpr-priority-template]'));
    var poolTemplates = asArray(carousel.querySelectorAll('template[data-zpr-pool-template]'));
    var priorityHandles = Object.create(null);
    var orderedTemplates = [];
    var nextTemplateIndex = 0;
    var isAppending = false;

    priorityTemplates.forEach(function (template) {
      var handle = template.getAttribute('data-review-handle');
      if (handle) {
        priorityHandles[handle] = true;
      }
      orderedTemplates.push(template);
    });

    var fallbackTemplates = poolTemplates.filter(function (template) {
      var handle = template.getAttribute('data-review-handle');
      return !handle || !priorityHandles[handle];
    });

    orderedTemplates = orderedTemplates.concat(shuffle(fallbackTemplates));

    if (orderedTemplates.length === 0) {
      carousel.hidden = true;
      return;
    }

    carousel.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

    function getCards() {
      return asArray(track.querySelectorAll(CARD_SELECTOR));
    }

    function hasMoreCards() {
      return nextTemplateIndex < orderedTemplates.length;
    }

    function getScrollStep() {
      var firstCard = getCards()[0];
      if (!firstCard) {
        return 1;
      }

      var cardWidth = firstCard.getBoundingClientRect().width;
      var trackStyles = window.getComputedStyle(track);
      var gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
      if (!isFinite(gap)) {
        gap = 0;
      }

      return cardWidth + gap;
    }

    function canScroll() {
      return track.scrollWidth > track.clientWidth + 2;
    }

    function updateControls() {
      var scrollingPossible = canScroll();
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;

      if (controls) {
        controls.hidden = !scrollingPossible && !hasMoreCards();
      }

      previousButton.disabled = !scrollingPossible || atStart;
      nextButton.disabled = !hasMoreCards() && (!scrollingPossible || atEnd);
    }

    function appendNextBatch() {
      if (isAppending || !hasMoreCards()) {
        return;
      }

      isAppending = true;
      var endIndex = Math.min(nextTemplateIndex + batchSize, orderedTemplates.length);
      var fragment = document.createDocumentFragment();

      while (nextTemplateIndex < endIndex) {
        var card = getTemplateCard(orderedTemplates[nextTemplateIndex]);
        nextTemplateIndex += 1;

        if (!card) {
          continue;
        }

        activateImage(card);
        fragment.appendChild(card);
      }

      track.appendChild(fragment);
      isAppending = false;
      updateControls();
    }

    function moveCarousel(direction) {
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var step = getScrollStep();
      var destination = track.scrollLeft + step * direction;

      if (direction > 0 && !canScroll() && hasMoreCards()) {
        appendNextBatch();
        step = getScrollStep();
        destination = track.scrollLeft + step;
      } else if (direction > 0 && track.scrollLeft + track.clientWidth >= track.scrollWidth - 3 && hasMoreCards()) {
        appendNextBatch();
        step = getScrollStep();
        destination = track.scrollLeft + step;
      }

      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: destination, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = destination;
      }
    }

    previousButton.addEventListener('click', function () {
      moveCarousel(-1);
    });

    nextButton.addEventListener('click', function () {
      moveCarousel(1);
    });

    var scrollFrame = 0;
    var resizeFrame = 0;

    track.addEventListener('scroll', function () {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(function () {
        scrollFrame = 0;
        if (!carousel.isConnected) return;
        var remainingDistance = track.scrollWidth - (track.scrollLeft + track.clientWidth);
        var threshold = Math.max(NEAR_END_PX, getScrollStep());
        if (remainingDistance <= threshold && hasMoreCards()) {
          appendNextBatch();
        } else {
          updateControls();
        }
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = 0;
        if (carousel.isConnected) updateControls();
      });
    }, { passive: true });

    appendNextBatch();
    updateControls();
  }

  var carouselObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        carouselObserver.unobserve(entry.target);
        entry.target.removeAttribute('data-zpr-observed');
        initializeCarousel(entry.target);
      }
    });
  }, { rootMargin: '400px 0px', threshold: 0 }) : null;

  function scheduleCarousel(carousel) {
    if (!carousel || carousel.getAttribute(INITIALIZED_ATTRIBUTE) === 'true') return;
    if (!carouselObserver || (window.Shopify && window.Shopify.designMode)) {
      initializeCarousel(carousel);
      return;
    }
    if (carousel.getAttribute('data-zpr-observed') === 'true') return;
    carousel.setAttribute('data-zpr-observed', 'true');
    carouselObserver.observe(carousel);
  }

  function initializeAllCarousels(scope) {
    var root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    var carousels = root.querySelectorAll(CAROUSEL_SELECTOR);

    asArray(carousels).forEach(scheduleCarousel);

    if (root.matches && root.matches(CAROUSEL_SELECTOR)) {
      scheduleCarousel(root);
    }
  }

  function start() {
    initializeAllCarousels(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) {
      initializeAllCarousels(event.target);
    }
  });
})();
